import React, { Component, ReactNode } from 'react';
import { useLocation } from 'wouter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary Component
 *
 * Catches React errors (including DOM errors from fullPage.js) and provides a fallback UI.
 * Automatically resets when navigating to a different page.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Special handling for DOM manipulation errors (e.g., from fullPage.js)
    if (error.message.includes('removeChild') || error.message.includes('insertBefore')) {
      console.warn('⚠️ DOM manipulation error caught - likely from fullPage.js unmount');
    }

    this.setState({
      error,
      errorInfo
    });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default Error Fallback UI
 */
interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  onReset: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, onReset }) => {
  const [, setLocation] = useLocation();

  const handleGoHome = () => {
    onReset();
    setLocation('/');
  };

  const isDOMError = error?.message.includes('removeChild') ||
                     error?.message.includes('insertBefore') ||
                     error?.message.includes('Node');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <h1 className="text-3xl font-montserrat font-bold text-gray-900 mb-4">
            Oops! Etwas ist schiefgelaufen
          </h1>

          <p className="text-lg text-gray-600 mb-6">
            {isDOMError
              ? 'Es gab ein Problem beim Wechseln der Seite. Keine Sorge, Ihre Daten sind sicher.'
              : 'Ein unerwarteter Fehler ist aufgetreten. Wir arbeiten daran, das Problem zu beheben.'
            }
          </p>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 mb-8">
            <button
              onClick={handleGoHome}
              className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              Zur Startseite
            </button>

            <button
              onClick={onReset}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-gray-400 transition-colors font-medium"
            >
              Erneut versuchen
            </button>
          </div>

          {/* Error Details (Development Mode Only) */}
          {import.meta.env.DEV && error && (
            <details className="text-left mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <summary className="cursor-pointer font-semibold text-red-800 mb-2">
                Technische Details (nur in Development sichtbar)
              </summary>
              <div className="mt-2 space-y-2">
                <div>
                  <h3 className="font-semibold text-red-700 mb-1">Error:</h3>
                  <pre className="text-xs text-red-600 bg-white p-2 rounded overflow-x-auto">
                    {error.toString()}
                  </pre>
                </div>
                {errorInfo && (
                  <div>
                    <h3 className="font-semibold text-red-700 mb-1">Component Stack:</h3>
                    <pre className="text-xs text-red-600 bg-white p-2 rounded overflow-x-auto whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Help Text */}
          <p className="text-sm text-gray-500 mt-6">
            Falls das Problem weiterhin besteht, kontaktieren Sie uns bitte unter{' '}
            <a href="mailto:verwaltung@yade-home.de" className="text-blue-600 hover:underline">
              verwaltung@yade-home.de
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook to reset error boundary when location changes
 * Wrap this around your ErrorBoundary to auto-reset on navigation
 */
export const ErrorBoundaryWithReset: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [location] = useLocation();
  const errorBoundaryRef = React.useRef<ErrorBoundary>(null);

  // Reset error when location changes
  React.useEffect(() => {
    if (errorBoundaryRef.current) {
      errorBoundaryRef.current.resetError();
    }
  }, [location]);

  return (
    <ErrorBoundary ref={errorBoundaryRef}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
